import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { PaginationProps } from './types';

const buildPages = (page: number, totalPages: number): Array<number | string> => {
  const maxVisible = 7;
  const pages: Array<number | string> = [];

  if (totalPages <= maxVisible) {
    for (let index = 1; index <= totalPages; index += 1) {
      pages.push(index);
    }
    return pages;
  }

  if (page <= 4) {
    return [1, 2, 3, 4, 5, '...', totalPages];
  }

  if (page >= totalPages - 3) {
    return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, '...', page - 1, page, page + 1, '...', totalPages];
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  page: pageProp,
  limit = 10,
  total = 0,
  totalPages,
  onPageChange,
  showInfo = true,
  labels,
  variant = 'full',
  containerClassName = '',
  mobileButtonClassName = 'relative inline-flex items-center px-4 py-2 border border-border-subtle text-sm font-medium rounded-lg text-gray-300 bg-bg-elevated hover:bg-bg-surface disabled:opacity-50 disabled:cursor-not-allowed transition-all',
  navClassName = 'relative z-0 inline-flex rounded-lg -space-x-px',
  navButtonClassName = 'relative inline-flex items-center px-2.5 py-2 border border-border-subtle bg-bg-elevated text-sm font-medium text-gray-500 hover:bg-bg-surface hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all',
  activePageClassName = 'z-10 bg-primary border-primary text-white',
  inactivePageClassName = 'bg-bg-elevated border-border-subtle text-gray-500 hover:bg-bg-surface hover:text-gray-300',
  infoClassName = 'text-sm text-gray-500',
  ellipsisClassName = 'relative inline-flex items-center px-4 py-2 border border-border-subtle bg-bg-elevated text-sm font-medium text-gray-500',
}) => {
  const page = currentPage || pageProp || 1;

  if (totalPages <= 1) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center justify-between ${containerClassName}`}>
        <span className="text-xs text-gray-400">{labels.total(total)}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="p-1.5 rounded text-gray-400 hover:text-gray-50 hover:bg-bg-elevated disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          {buildPages(page, totalPages).map((value, index) =>
            value === '...' ? (
              <span key={`ellipsis-${index}`} className="w-8 text-center text-gray-500 text-xs">
                …
              </span>
            ) : (
              <button
                key={value}
                onClick={() => onPageChange(value as number)}
                className={`w-8 h-8 text-xs rounded transition-colors cursor-pointer ${value === page ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-50 hover:bg-bg-elevated'}`}
              >
                {value}
              </button>
            )
          )}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="p-1.5 rounded text-gray-400 hover:text-gray-50 hover:bg-bg-elevated disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className={containerClassName}>
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className={mobileButtonClassName}
        >
          {labels.previous}
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className={`ml-3 ${mobileButtonClassName}`}
        >
          {labels.next}
        </button>
      </div>

      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        {showInfo && (
          <div>
            <p className={infoClassName}>{labels.showing({ start: startItem, end: endItem, total })}</p>
          </div>
        )}
        <div>
          <nav className={navClassName}>
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className={`${navButtonClassName} rounded-l-lg`}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            {buildPages(page, totalPages).map((value, index) =>
              value === '...' ? (
                <span key={`ellipsis-${index}`} className={ellipsisClassName}>
                  ...
                </span>
              ) : (
                <button
                  key={value}
                  onClick={() => onPageChange(value as number)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all ${value === page ? activePageClassName : inactivePageClassName}`}
                >
                  {value}
                </button>
              )
            )}
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className={`${navButtonClassName} rounded-r-lg`}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
